import { Text } from "@olivahealth/oli-ui";
import React, { type ReactElement, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { format } from "date-fns";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { FeatureFlagsKeys } from "@olivahealth/utils/types/FeatureFlags";
import { MobileAppDownloader } from "@olivahealth/oli-ui/src/organisms/CompanionMobileAppDownloadBanner";
import { useUserData } from "../../../services/contexts/UserDataContext";
import useTranslation from "../../../hooks/useTranslation";
import { formatDate } from "../../../utils/date/format";
import CompanionSessionListWidget from "../../organisms/CompanionSessionListWidget";
import PopularTopicsCompanionWidget from "../../organisms/PopularTopicsCompanionWidget";
import PopularResources from "../../organisms/PopularResources";
import UpcomingEvents from "../EmployeeDashboard/UpcomingEvents";
import EmployeeNPSSurvey from "../EmployeeDashboard/EmployeeNPSSurvey";
import { DarkModePageLayout } from "../../components/DarkModePageLayout";
import { CompanionContent_data$key } from "./__generated__/CompanionContent_data.graphql";
import { CompanionWidget } from "./CompanionWidget";
import * as s from "./styles";

const CompanionContentFragment = graphql`
  fragment CompanionContent_data on Query
  @argumentDefinitions(userId: { type: "String!" }) {
    ...UpcomingEventsFragment_data @arguments(userId: $userId)
    threads(first: 1, type: WELLBEING) {
      edges {
        __typename
      }
    }
    wellbeingProfileResults(filterBy: { userId: $userId }, first: 1) {
      nodes {
        __typename
      }
    }
  }
`;

export function CompanionContent({
  data,
}: {
  data: CompanionContent_data$key;
}): ReactElement {
  const fragmentData = useFragment(CompanionContentFragment, data);
  const { data: user } = useUserData();
  const { t } = useTranslation("companion");
  const showMobileAppDownloadBanner = !user?.deviceTokens?.length;
  const isCompanionPageEnabled = useFeatureIsOn(
    `${FeatureFlagsKeys.COMPANION_PAGE}`,
  );

  const hasThreads = fragmentData.threads.edges.length > 0;
  const hasWellbeingProfile =
    fragmentData.wellbeingProfileResults.nodes.length > 0;
  const showPopularTopics = hasThreads; // first thread should be filling out WBP, not a popular topic

  const titlesArray = t("form.titles", {
    userName: user?.name ?? "",
    returnObjects: true,
  }) as unknown as string[];
  const [randomTitle] = useState(() => {
    const randomIndex = Math.floor(Math.random() * titlesArray.length);
    return titlesArray[randomIndex];
  });

  return (
    <DarkModePageLayout>
      <div className={s.topContainer}>
        {isCompanionPageEnabled ? (
          <>
            <div className="flex flex-col gap-y-8 text-white">
              {hasThreads ? (
                <div className="flex flex-col gap-y-3 items-start">
                  <Text color="accent-lighter">{formatDate(new Date())}</Text>
                  <Text variant="h2" color="white">
                    {randomTitle}
                  </Text>
                </div>
              ) : (
                <div className="flex flex-col gap-y-3 items-start">
                  <Text color="accent-lighter">{formatDate(new Date())}</Text>
                  <Text variant="h2" color="white">
                    {t("welcomeTitle", { userName: user?.name ?? "" })}
                  </Text>
                </div>
              )}
              <CompanionWidget
                variant="textarea"
                hasThreads={hasThreads}
                hasWellbeingProfile={hasWellbeingProfile}
                from="default"
                threadType="WELLBEING"
                placeholder={t("form.placeholder")}
              />
            </div>
            {showPopularTopics && (
              <PopularTopicsCompanionWidget threadType="WELLBEING" />
            )}
          </>
        ) : (
          <div className="flex flex-col gap-y-8 text-white">
            <div className="flex flex-col gap-y-3 items-start">
              <Text variant="body" color="accent-lighter">
                {format(new Date(), "EEEE, d MMMM")}
              </Text>
              <Text variant="h2" color="white">
                {t("welcomeTitle", { userName: user?.name ?? "" })}
              </Text>
            </div>
          </div>
        )}
      </div>

      <div className={s.bottomContainer}>
        <EmployeeNPSSurvey />
        {fragmentData && (
          <UpcomingEvents data={fragmentData} addSpaceAfter={false} />
        )}
        {isCompanionPageEnabled ? <CompanionSessionListWidget /> : null}
        <PopularResources showSeeAllButton />
        {showMobileAppDownloadBanner && <MobileAppDownloader />}
      </div>
    </DarkModePageLayout>
  );
}
